/*
  PDP — the review strip that rides above Kaching's sticky add-to-cart bar.

  ---------- Why this is our element and not an injection ----------

  The obvious approach is to append a node inside <kaching-bundles-sticky-atc>.
  Do not. Inspected on this page, that element is a registered custom element
  rendering a Vue tree — its markup is full of `<!---->` placeholder comments —
  and it re-renders when the variant changes, which is precisely the moment this
  bar updates. Anything appended is wiped, silently, and only sometimes. That is
  the worst kind of bug: it works when you test it.

  So this strip is our own fixed element, mounted at the end of <body> and owned
  entirely by us. The only thing it takes from Kaching is a measurement — how
  tall their bar is and whether it is visible — which it reads from the outside
  with a ResizeObserver and by reading their computed style.

  The failure mode is therefore mild and visible rather than severe and silent:
  if Kaching changes their markup, the worst case is that the strip sits at the
  wrong offset or stops hiding itself. It cannot disappear, and it cannot take
  the buy button with it.

  ---------- What it does ----------

  Shows the product's rating and review count, and jumps to the review wall when
  tapped. The numbers come from Liquid (the Loox metafields the buy box already
  reads), so they are correct on first paint and never go stale.

  Mobile only, by CSS. Desktop has the rating in the buy box and does not carry
  a sticky bar.
*/

(function () {
  'use strict';

  var HOST = 'kaching-bundles-sticky-atc';

  class PdpStickyReviews extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-sticky-reviews-link]');
      if (!this.link) return;

      this.onClick = this.jump.bind(this);
      this.link.addEventListener('click', this.onClick);

      // The bar can mount after us — Kaching's script is async — so wait for it
      // rather than assuming it is already there.
      this.findBar();
    }

    disconnectedCallback() {
      this.link?.removeEventListener('click', this.onClick);
      this.resizeObserver?.disconnect();
      this.attributeObserver?.disconnect();
      this.mountObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    findBar() {
      var bar = document.querySelector(HOST);
      if (bar) {
        this.attach(bar);
        return;
      }

      // Not mounted yet. Watch for it once, then stop — a permanent observer on
      // the whole body is a cost paid on every DOM change for the life of the
      // page.
      if (!window.MutationObserver) return;

      this.mountObserver = new MutationObserver(function () {
        var found = document.querySelector(HOST);
        if (!found) return;
        this.mountObserver.disconnect();
        this.mountObserver = null;
        this.attach(found);
      }.bind(this));

      this.mountObserver.observe(document.body, { childList: true, subtree: true });
    }

    attach(bar) {
      this.bar = bar;

      // ---- height ----
      //
      // Their bar's height is not a constant: it grows when the product title
      // wraps to two lines, which it does on a narrow phone. Measured rather
      // than hard-coded, so the strip sits on top of it at any width.
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.measure.bind(this));
        this.resizeObserver.observe(bar);
      }

      // ---- visibility ----
      //
      // NOT IntersectionObserver, which was the first thing tried and is wrong
      // here. Their bar is position:fixed at the bottom of the viewport with a
      // constant height, and it shows and hides by changing opacity — so it is
      // geometrically intersecting the viewport from the moment it mounts and
      // no threshold is ever crossed. Measured: a fresh observer on it reports
      // isIntersecting:true once and never fires again, so the strip caught a
      // single `false` at insertion (before layout) and stayed hidden forever.
      //
      // So read the rendered state instead, which covers every way an element
      // can be hidden, and re-read it on the two things that can change it:
      //
      //   - an attribute change on the bar (their visible-class toggle)
      //   - a scroll, which is what triggers that toggle in the first place
      //
      // Reading computed style rather than testing for their class name keeps
      // this working if they rename it.
      this.sync = this.sync.bind(this);

      if (window.MutationObserver) {
        this.attributeObserver = new MutationObserver(this.sync);
        this.attributeObserver.observe(bar, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }

      // Passive and rAF-coalesced: this fires on every scroll frame otherwise,
      // and it reads layout.
      this.onScroll = function () {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(function () {
          this.frame = null;
          this.sync();
        }.bind(this));
      }.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.sync();
      this.measure();
    }

    measure() {
      if (!this.bar) return;
      var height = Math.round(this.bar.getBoundingClientRect().height);
      this.style.setProperty('--sticky-bar-height', height + 'px');
    }

    /**
     * True when the bar is actually on screen — not merely present in the DOM.
     * Checks the three ways it can be hidden, because their bar uses opacity
     * and a future version might use any of them.
     */
    sync() {
      if (!this.bar) return;

      var style = window.getComputedStyle(this.bar);
      var visible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        parseFloat(style.opacity) > 0.01 &&
        this.bar.getBoundingClientRect().height > 0;

      this.measure();
      this.classList.toggle('is-visible', visible);
    }

    /**
     * Smooth-scrolls to the review wall. Falls back to the anchor's own
     * behaviour if the target has not rendered — Loox mounts late, and a jump
     * to nothing is worse than a jump to the bottom of the page.
     */
    jump(event) {
      var id = this.link.getAttribute('href') || '';
      if (id.charAt(0) !== '#') return;

      var target = document.querySelector(id);
      if (!target) return;

      event.preventDefault();

      // Offset by the sticky header, so the reviews heading is not left
      // underneath it.
      var header = document.querySelector('.shopify-section-header, [class*="header-container"]');
      var offset = header ? header.getBoundingClientRect().height : 0;
      var top = target.getBoundingClientRect().top + window.scrollY - offset - 12;

      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  if (!window.customElements.get('v2-pdp-sticky-reviews')) {
    window.customElements.define('v2-pdp-sticky-reviews', PdpStickyReviews);
  }
})();
